import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  formatAddressShort,
  formatCents,
  formatDate,
  formatWindow,
  JOB_STATUS_LABELS,
  JOB_STATUSES,
  type Job,
  type JobStatus,
} from '@fsm/shared';
import { keys } from '../lib/queries';
import { api } from '../lib/api';
import { ListFoot, usePagedList } from '../components/PagedList';
import { useAuth } from '../lib/auth';
import { PageFooter } from '../components/Layout';
import {
  Avatar,
  Button,
  Card,
  Dot,
  EmptyState,
  FilterTabs,
  PageHeader,
  SearchField,
  sortRows,
  SortTh,
  Spinner,
  StatusBadge,
  Table,
  Td,
  Th,
  useTableSort,
} from '../components/ui';
import { NewJobModal } from '../components/NewJobModal';
import {
  ConfirmDeleteModal,
  SelectCell,
  SelectionBar,
  useSelection,
} from '../components/Selection';
import { useQueryClient } from '@tanstack/react-query';

export function JobsPage() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<JobStatus | 'all'>('all');
  const [creating, setCreating] = useState(false);

  const query = useMemo(
    () => ({
      q: q.trim() || undefined,
      status: status === 'all' ? undefined : status,
    }),
    [q, status],
  );

  const list = usePagedList<Job>({
    queryKey: keys.jobs(query),
    fetchPage: (cursor, limit) => api.jobs.list({ ...query, cursor, limit }),
  });
  const jobs = list.items;
  const isLoading = list.isLoading;

  const sort = useTableSort<
    'number' | 'title' | 'customer' | 'scheduled' | 'tech' | 'value' | 'status'
  >('number', 'desc');
  const sorted = sortRows(jobs, sort, (job, key) => {
    switch (key) {
      case 'number':
        return job.number;
      case 'title':
        return job.title;
      case 'customer':
        return job.customer?.name ?? null;
      // The raw timestamp, not the formatted date: "Sep 2" and "Oct 10" sort
      // alphabetically into nonsense.
      case 'scheduled':
        return job.scheduledStart ?? null;
      case 'tech':
        return job.assignedTech?.name ?? null;
      case 'value':
        return job.totals?.totalCents ?? 0;
      case 'status':
        return JOB_STATUS_LABELS[job.status];
    }
  });

  /* Ticking rows and deleting them — see components/Selection.tsx. */
  const selection = useSelection(sorted);
  const [confirming, setConfirming] = useState(false);
  const qc = useQueryClient();

  return (
    <>
      <PageHeader
        eyebrow="Workspace"
        title="Jobs"
        subtitle={`${list.total.toLocaleString()} shown`}
        actions={
          <>
            <SearchField
              value={q}
              onChange={setQ}
              placeholder="Search jobs, customers, addresses"
              className="w-[280px]"
            />
            {/* See the note on the customers screen. */}
            {can('admin') && (
              <Button icon="file" onClick={() => navigate('/import?type=jobs')}>
                Import
              </Button>
            )}
            <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>
              New job
            </Button>
          </>
        }
        tabs={
          <FilterTabs
            value={status}
            onChange={setStatus}
            options={(['all', ...JOB_STATUSES] as const).map((value) => ({
              value,
              label: value === 'all' ? 'All' : JOB_STATUS_LABELS[value],
            }))}
          />
        }
      />

      <div className="min-h-0 flex-1 overflow-y-auto px-[30px]">
        <div className="mx-auto max-w-[1860px]">
          <Card
            title={status === 'all' ? 'All jobs' : JOB_STATUS_LABELS[status]}
            icon="job"
            count={jobs.length}
            divided
            bodyClassName="p-0"
          >
            {isLoading ? (
              <div className="grid place-items-center py-16">
                <Spinner className="size-5 text-ink-4" />
              </div>
            ) : jobs.length === 0 ? (
              <EmptyState
                title="No jobs match"
                description="Try a different search, or clear the status filter."
              />
            ) : (
              <>
                <SelectionBar
                  count={selection.count}
                  one="job"
                  many="jobs"
                  onClear={selection.clear}
                  onDelete={() => setConfirming(true)}
                />
                <Table>
                <thead>
                  <tr>
                    {can('admin') && (
                      <Th className="w-[44px]">
                        <SelectCell
                          checked={selection.allVisible}
                          indeterminate={selection.someVisible}
                          onChange={selection.toggleAll}
                          label="Select all jobs"
                        />
                      </Th>
                    )}
                    <SortTh sortKey="title" sort={sort} className="w-[32%]">Job</SortTh>
                    <SortTh sortKey="customer" sort={sort}>Customer</SortTh>
                    <SortTh sortKey="scheduled" sort={sort}>Scheduled</SortTh>
                    <SortTh sortKey="tech" sort={sort}>Tech</SortTh>
                    <SortTh sortKey="value" sort={sort}>Value</SortTh>
                    <SortTh sortKey="status" sort={sort} align="right">Status</SortTh>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((job) => (
                    <tr
                      key={job.id}
                      onClick={() => navigate(`/jobs/${job.id}`)}
                      className="cursor-pointer transition-colors hover:bg-raised"
                    >
                      {can('admin') && (
                        <Td>
                          <SelectCell
                            checked={selection.isSelected(job.id)}
                            onChange={() => selection.toggle(job.id)}
                            label={`Select ${job.id}`}
                          />
                        </Td>
                      )}
                      <Td>
                        <div className="text-[14px] font-semibold text-ink">{job.title}</div>
                        <div className="mt-1 text-[12px] text-ink-4">
                          #{job.number}
                          <Dot />
                          {formatAddressShort(job.property)}
                        </div>
                      </Td>
                      <Td>
                        <Link
                          to={`/customers/${job.customerId}`}
                          onClick={(e) => e.stopPropagation()}
                          className="hover:text-accent"
                        >
                          {job.customer?.name}
                        </Link>
                      </Td>
                      <Td className="tnum">
                        {job.scheduledStart && job.scheduledEnd ? (
                          <>
                            <div>{formatDate(job.scheduledStart)}</div>
                            <div className="mt-1 text-[12px] text-ink-4">
                              {formatWindow(job.scheduledStart, job.scheduledEnd)}
                            </div>
                          </>
                        ) : (
                          <span className="text-ink-4">Not scheduled</span>
                        )}
                      </Td>
                      <Td>
                        {job.assignedTech ? (
                          <span className="flex items-center gap-2">
                            <Avatar
                              name={job.assignedTech.name}
                              color={job.assignedTech.colorHex}
                              size={24}
                            />
                            <span>{job.assignedTech.name.split(' ')[0]}</span>
                          </span>
                        ) : (
                          <span className="text-ink-4">Unassigned</span>
                        )}
                      </Td>
                      <Td className="tnum">{formatCents(job.totals?.totalCents ?? 0)}</Td>
                      <Td align="right">
                        <StatusBadge status={job.status} />
                      </Td>
                    </tr>
                  ))}
                </tbody>
                </Table>
                <ListFoot list={list} one="job" many="jobs" />
            </>
            )}
          </Card>
        </div>

        <PageFooter label={`${jobs.length} jobs`} />
      </div>

      <NewJobModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(job) => navigate(`/jobs/${job.id}`)}
      />

      <ConfirmDeleteModal
        open={confirming}
        onClose={() => setConfirming(false)}
        count={selection.count}
        one="job"
        many="jobs"
        warning="Line items, photos, notes and status history go with each job. A job that has been invoiced cannot be deleted."
        onConfirm={() => api.jobs.bulkRemove(selection.selected)}
        onDeleted={() => {
          void qc.invalidateQueries({ queryKey: ['jobs'] });
          selection.clear();
        }}
      />

    </>
  );
}
